"use client";
import { defaultCompanionKey } from "@/lib/companions";
import { getCurrentDocId, setCurrentDocId } from "@/lib/storage/local-idb";
import { useStorage } from "@/lib/storage/provider";
import {
  EditorCommand,
  EditorCommandEmpty,
  EditorCommandItem,
  EditorCommandList,
  EditorContent,
  type EditorInstance,
  EditorRoot,
  ImageResizer,
  type JSONContent,
  handleCommandNavigation,
  handleImageDrop,
  handleImagePaste,
} from "novel";
import { useCallback, useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { editorExtensions } from "./extensions";
import GhostBranching from "./ghost/ghost-branching";
import { ColorSelector } from "./selectors/color-selector";
import { LinkSelector } from "./selectors/link-selector";
import { MathSelector } from "./selectors/math-selector";
import { NodeSelector } from "./selectors/node-selector";
import { Separator } from "./ui/separator";

import AssetResolver from "./assets/asset-resolver";
import EmbedBridge from "./embed/embed-bridge";
import GenerativeMenuSwitch from "./generative/generative-menu-switch";
import { makeUploadFn } from "./image-upload-adapter";
import { TextButtons } from "./selectors/text-buttons";
import { slashCommand, suggestionItems } from "./slash-command";
import EditorToolbar from "./ui/editor-toolbar";
import FilesSidebar from "./ui/files-sidebar";

const hljs = require("highlight.js");

const extensions = [...editorExtensions, slashCommand];

const TailwindAdvancedEditor = ({
  companionKey = defaultCompanionKey,
  sidebarEnabled = true,
}: { companionKey?: string; sidebarEnabled?: boolean }) => {
  const [initialContent, setInitialContent] = useState<null | JSONContent>(null);
  const [saveStatus, setSaveStatus] = useState("Saved");
  const [charsCount, setCharsCount] = useState();
  const { adapter } = useStorage();
  const [docId, setDocId] = useState<string | null>(null);

  const [openNode, setOpenNode] = useState(false);
  const [openColor, setOpenColor] = useState(false);
  const [openLink, setOpenLink] = useState(false);
  const [openAI, setOpenAI] = useState(false);
  const [filesOpen, setFilesOpen] = useState(false);
  useEffect(() => {
    try {
      window.localStorage.setItem("companionKey", companionKey);
    } catch {}
  }, [companionKey]);

  // Allow parent (embed host) to request opening a specific doc id
  useEffect(() => {
    const onMsg = async (e: MessageEvent) => {
      const d = e.data as { type?: string; id?: string };
      if (d?.type !== "ploom:open-id" || !d.id) return;
      try {
        const doc = await adapter.read(d.id);
        if (doc) {
          setDocId(doc.id);
          setCurrentDocId(doc.id);
          setInitialContent(doc.json as JSONContent);
        }
      } catch {}
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [adapter]);

  // Handler for embed open messages (must be declared before early return to keep hook order stable)
  const handleEmbedOpen = useCallback((id: string | null, json: JSONContent) => {
    if (id) {
      setDocId(id);
      setCurrentDocId(id);
    } else {
      setDocId(null);
    }
    setInitialContent(json);
  }, []);

  //Apply Codeblock Highlighting on the HTML from editor.getHTML()
  const highlightCodeblocks = (content: string) => {
    const doc = new DOMParser().parseFromString(content, "text/html");
    doc.querySelectorAll("pre code").forEach((el) => {
      // @ts-ignore
      // https://highlightjs.readthedocs.io/en/latest/api.html?highlight=highlightElement#highlightelement
      hljs.highlightElement(el);
    });
    return new XMLSerializer().serializeToString(doc);
  };

  const debouncedUpdates = useDebouncedCallback(async (editor: EditorInstance) => {
    const json = editor.getJSON();
    setCharsCount(editor.storage.characterCount.words());
    window.localStorage.setItem("html-content", highlightCodeblocks(editor.getHTML()));
    window.localStorage.setItem("novel-content", JSON.stringify(json));
    window.localStorage.setItem("markdown", editor.storage.markdown.getMarkdown());
    try {
      window.localStorage.setItem("lastSavedAt", String(Date.now()));
    } catch {}
    try {
      if (docId) {
        await adapter.save(docId, {
          json,
          markdown: editor.storage.markdown.getMarkdown(),
          html: highlightCodeblocks(editor.getHTML()),
          title: extractTitleFromEditor(editor),
        });
      }
    } catch (e) {
      console.error("save failed", e);
    }
    setSaveStatus("Saved");
  }, 500);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const current = getCurrentDocId();
      if (!current) {
        const created = await adapter.create({ title: "Untitled" });
        setCurrentDocId(created.id);
        if (!mounted) return;
        setDocId(created.id);
        setInitialContent(created.json as JSONContent);
        return;
      }
      const doc = await adapter.read(current);
      if (!doc) {
        const created = await adapter.create({ title: "Untitled" });
        setCurrentDocId(created.id);
        if (!mounted) return;
        setDocId(created.id);
        setInitialContent(created.json as JSONContent);
        return;
      }
      if (!mounted) return;
      setDocId(doc.id);
      setInitialContent(doc.json as JSONContent);
    })();
    return () => {
      mounted = false;
    };
  }, [adapter]);

  if (!initialContent) return null;

  return (
    <div className="relative w-full max-w-screen-lg flex flex-col items-center gap-3">
      {sidebarEnabled && (
        <FilesSidebar
          open={filesOpen}
          onOpenChange={setFilesOpen}
          onOpenDoc={async (id) => {
            try {
              const d = await adapter.read(id);
              if (d) {
                setDocId(d.id);
                setCurrentDocId(d.id);
                setInitialContent(d.json as JSONContent);
              }
            } catch (e) {
              console.error(e);
            }
          }}
        />
      )}
      <EditorRoot>
        <EditorContent
          key={docId ?? "default"}
          initialContent={initialContent}
          extensions={extensions}
          className="relative sm:mb-[calc(8vh)] mx-auto bg-white sm:rounded-md sm:border border-neutral-200 sm:shadow-xl w-full sm:w-[860px] min-h-[1056px] p-8 mt-32"
          editorProps={{
            handleDOMEvents: {
              keydown: (_view, event) => handleCommandNavigation(event),
            },
            handlePaste: (view, event) => handleImagePaste(view, event, makeUploadFn(adapter)),
            handleDrop: (view, event, _slice, moved) => handleImageDrop(view, event, moved, makeUploadFn(adapter)),
            attributes: {
              class: "prose prose-lg prose-headings:font-title font-default focus:outline-none max-w-full",
            },
          }}
          onUpdate={({ editor }) => {
            debouncedUpdates(editor);
            setSaveStatus("Unsaved");
          }}
          slotAfter={<ImageResizer />}
        >
          <EmbedBridge onOpen={handleEmbedOpen} />
          <div className="fixed left-0 right-0 top-0 z-30 px-4 py-2 pointer-events-auto">
            <EditorToolbar
              saveStatus={saveStatus}
              words={Number(charsCount) || 0}
              showFilesButton={sidebarEnabled}
              showImportExport={true}
              showSettings={true}
              onToggleFiles={() => setFilesOpen((v) => !v)}
            />
          </div>
          {/* Spacer to offset fixed toolbar height */}
          <div className="h-[120px] sm:h-[100px]" />
          <AssetResolver />
          {/* Ghost branching autocomplete overlay (non-insertable) */}
          <GhostBranching personaKey={companionKey} />
          <EditorCommand className="z-50 h-auto max-h-[330px] overflow-y-auto rounded-md border border-muted bg-background px-1 py-2 shadow-md transition-all">
            <EditorCommandEmpty className="px-2 text-muted-foreground">No results</EditorCommandEmpty>
            <EditorCommandList>
              {suggestionItems.map((item) => (
                <EditorCommandItem
                  value={item.title}
                  onCommand={(val) => item.command(val)}
                  className="flex w-full items-center space-x-2 rounded-md px-2 py-1 text-left text-sm hover:bg-accent aria-selected:bg-accent"
                  key={item.title}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-md border border-muted bg-background">
                    {item.icon}
                  </div>
                  <div>
                    <p className="font-medium">{item.title}</p>
                    <p className="text-xs text-muted-foreground">{item.description}</p>
                  </div>
                </EditorCommandItem>
              ))}
            </EditorCommandList>
          </EditorCommand>

          <GenerativeMenuSwitch open={openAI} onOpenChange={setOpenAI}>
            <Separator orientation="vertical" />
            <NodeSelector open={openNode} onOpenChange={setOpenNode} />
            <Separator orientation="vertical" />

            <LinkSelector open={openLink} onOpenChange={setOpenLink} />
            <Separator orientation="vertical" />
            <MathSelector />
            <Separator orientation="vertical" />
            <TextButtons />
            <Separator orientation="vertical" />
            <ColorSelector open={openColor} onOpenChange={setOpenColor} />
          </GenerativeMenuSwitch>
        </EditorContent>
      </EditorRoot>
    </div>
  );
};

export default TailwindAdvancedEditor;

function extractTitleFromEditor(editor: EditorInstance): string {
  try {
    const md = editor.storage.markdown.getMarkdown() as string;
    const first = (md.split(/\r?\n/).find((l: string) => l.trim().length) ?? "Untitled").trim();
    return first.replace(/^#+\s*/, "").slice(0, 140) || "Untitled";
  } catch {
    return "Untitled";
  }
}
